import React, { useState, useEffect } from "react";
import {
  Box,
  SimpleGrid,
  Select,
  Button,
  Input,
  useColorModeValue,
  Text,
  Flex,
  Stack,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, Line, PieChart, Pie, Cell } from "recharts";
import { useTranslation } from "react-i18next";

export default function AiDashboard() {
  const { t } = useTranslation();
  const brandColor = useColorModeValue("cyan.500", "white");
  const boxBg = useColorModeValue("white", "whiteAlpha.100");

  const [timeFilter, setTimeFilter] = useState("last_month");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [specificDate, setSpecificDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [chatTrendsData, setChatTrendsData] = useState(null); // New state for chat trends
  const [messagesPerDay, setMessagesPerDay] = useState([]); // New state for messages per day

  const handleFilterChange = (event) => {
    setTimeFilter(event.target.value);
  };

  const handleApplyFilter = async () => {
    const baseUrl = `${process.env.REACT_APP_API_URL3}/analytics/dashboard`;
    const params = new URLSearchParams({ time_filter_type: timeFilter });

    if (timeFilter === "specific_month") {
      if (year) params.append("year", year);
      if (month) params.append("month", month);
    } else if (timeFilter === "specific_date") {
      if (specificDate) params.append("date", specificDate);
    } else if (timeFilter === "custom_range") {
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
    }

    const url = `${baseUrl}?${params.toString()}`;
    console.log("Request URL:", url);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch analytics data");
      }
      const data = await response.json();
      setAnalyticsData(data);
      console.log("Analytics Data:", data);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    }
    fetchChatTrends(); // Fetch chat trends data after applying filter
    fetchChatSummary(); // Fetch chat summary data after applying filter
  };

  const fetchChatTrends = async () => {
    const baseUrl = `${process.env.REACT_APP_API_URL3}/analytics/chats/trends`;
    const params = new URLSearchParams({ time_filter_type: timeFilter });

    if (timeFilter === "specific_month") {
      if (year) params.append("year", year);
      if (month) params.append("month", month);
    } else if (timeFilter === "specific_date") {
      if (specificDate) params.append("date", specificDate);
    } else if (timeFilter === "custom_range") {
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
    }

    const url = `${baseUrl}?${params.toString()}`;
    console.log("Chat Trends Request URL:", url);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch chat trends data");
      }
      const result = await response.json();
      setChatTrendsData(result.data); // Ensure only the `data` array is set
      console.log("Chat Trends Data:", result.data);
    } catch (error) {
      console.error("Error fetching chat trends data:", error);
    }
  };

  const fetchChatSummary = async () => {
    const baseUrl = `${process.env.REACT_APP_API_URL3}/analytics/chats/summary`;
    const params = new URLSearchParams({ time_filter_type: timeFilter });

    if (timeFilter === "specific_month") {
      if (year) params.append("year", year);
      if (month) params.append("month", month);
    } else if (timeFilter === "specific_date") {
      if (specificDate) params.append("date", specificDate);
    } else if (timeFilter === "custom_range") {
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
    }

    const url = `${baseUrl}?${params.toString()}`;
    console.log("Chat Summary Request URL:", url);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch chat summary data");
      }
      const result = await response.json();

      // Process data for histogram
      const messagesByDay = result.breakdowns.by_day_of_week.map((item) => ({
        day: item.category,
        count: item.count,
      }));

      setMessagesPerDay(messagesByDay); // Save processed data to state
      console.log("Messages Per Day:", messagesByDay);
    } catch (error) {
      console.error("Error fetching chat summary data:", error);
    }
  };

  const renderDateFilters = () => {
    switch (timeFilter) {
      case "specific_month":
        return (
          <Stack direction="row" spacing="10px">
            <Input
              placeholder={t("Year")}
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
            <Input
              placeholder={t("Month")}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </Stack>
        );
      case "specific_date":
        return (
          <Input
            type="date"
            value={specificDate}
            onChange={(e) => setSpecificDate(e.target.value)}
            width={{ base: "100%", md: "200px" }}
          />
        );
      case "custom_range":
        return (
          <Stack direction="row" spacing="10px">
            <Input
              type="date"
              placeholder={t("Start Date")}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              type="date"
              placeholder={t("End Date")}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Stack>
        );
      default:
        return null;
    }
  };

  const aggregateByMonth = (data) => {
    const monthlyData = {};

    data.forEach((item) => {
      const month = new Date(item.date).getMonth() + 1; // Extract month (1-based index)
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          chat_count: 0,
          message_count: 0,
          user_token_count: 0,
          assistant_token_count: 0,
        };
      }
      monthlyData[month].chat_count += item.chat_count;
      monthlyData[month].message_count += item.message_count;
      monthlyData[month].user_token_count += item.user_token_count;
      monthlyData[month].assistant_token_count += item.assistant_token_count;
    });

    return Object.values(monthlyData); // Convert object to array
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Filter Section */}
      <Box mb="20px" p="20px" bg={boxBg} borderRadius="md" boxShadow="sm">
        <Text fontSize="lg" fontWeight="bold" mb="10px">
          {t("Filter Analytics")}
        </Text>
        <Flex direction={{ base: "column", md: "row" }} gap="20px" align="center">
          <Select
            value={timeFilter}
            onChange={handleFilterChange}
            placeholder={t("Select Time Filter")}
            width={{ base: "100%", md: "200px" }}
          >
            <option value="last_month">{t("Last Month")}</option>
            <option value="last_week">{t("Last Week")}</option>
            <option value="last_year">{t("Last Year")}</option>
            <option value="specific_month">{t("Specific Month")}</option>
            <option value="specific_date">{t("Specific Date")}</option>
            <option value="custom_range">{t("Custom Date Range")}</option>
          </Select>
          
          {/* Conditional Inputs for Filters */}
          {renderDateFilters()}

          <Button colorScheme="blue" onClick={handleApplyFilter}>
            {t("Apply Filter")}
          </Button>
        </Flex>
      </Box>

      {/* Analytics Data */}
      {analyticsData && (
        <Box>
          {/* Time Period */}
          <Text fontSize="lg" fontWeight="bold" mb="10px">
            {t("Time Period")}: {analyticsData.time_period.description}
          </Text>

          {/* Chat Metrics */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px" mb="20px">
            <MiniStatistics
              name={t("Total Chats")}
              value={analyticsData.chat_metrics.total_chats || "N/A"}
            />
            <MiniStatistics
              name={t("Total Messages")}
              value={analyticsData.chat_metrics.total_messages || "N/A"}
            />
            <MiniStatistics
              name={t("Total Tokens")}
              value={analyticsData.chat_metrics.total_tokens || "N/A"}
            />
            <MiniStatistics
              name={t("User Tokens")}
              value={analyticsData.chat_metrics.user_tokens || "N/A"}
            />
            <MiniStatistics
              name={t("Assistant Tokens")}
              value={analyticsData.chat_metrics.assistant_tokens || "N/A"}
            />
            <MiniStatistics
              name={t("Avg Tokens/Message")}
              value={analyticsData.chat_metrics.avg_tokens_per_message || "N/A"}
            />
          </SimpleGrid>

          {/* Feedback Metrics */}
          <Box mb="20px" p="20px" bg={boxBg} borderRadius="3xl" boxShadow="md">
            <Text fontSize="xl" fontWeight="bold" mb="10px" borderBottom="2px solid" borderColor={brandColor}>
              {t("Feedback Metrics")}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
              {/* Good vs. Bad Feedback */}
              <Box p="10px" bg={boxBg} borderRadius="md" boxShadow="sm">
                <Text fontSize="md" fontWeight="bold" mb="5px" textAlign="center">
                  {t("Good vs. Bad Feedback")}
                </Text>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[

                        { type: t("Good"), value: analyticsData.feedback_metrics.sentiment.good },
                        { type: t("Bad"), value: analyticsData.feedback_metrics.sentiment.bad },
                      ]}
                      dataKey="value"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      <Cell fill="#82ca9d" /> {/* Good */}
                      <Cell fill="#ff7373" /> {/* Bad */}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: boxBg,
                        borderRadius: "8px",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                      }}
                      formatter={(value, name) => [`${value}`, `${name}`]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              {/* High vs. Low Priority Feedback */}
              <Box p="10px" bg={boxBg} borderRadius="md" boxShadow="sm">
                <Text fontSize="md" fontWeight="bold" mb="5px" textAlign="center">
                  {t("High vs. Low Priority Feedback")}
                </Text>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { type: t("High"), value: analyticsData.feedback_metrics.priority.high },
                        { type: t("Low"), value: analyticsData.feedback_metrics.priority.low },
                      ]}
                      dataKey="value"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      <Cell fill="#8884d8" /> {/* High */}
                      <Cell fill="#ffc658" /> {/* Low */}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: boxBg,
                        borderRadius: "8px",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                      }}
                      formatter={(value, name) => [`${value}`, `${name}`]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Feedback Trend Chart */}
          <Box mb="20px" p="20px" bg={boxBg} borderRadius="3xl" boxShadow="md">
            <Text fontSize="xl" fontWeight="bold" mb="10px" borderBottom="2px solid" borderColor={brandColor}>
              {t("Feedback Trend")}
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={analyticsData.trends.feedback}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="good" name="Positive" fill="#82ca9d" />
                <Bar dataKey="bad" name="Negative" fill="#ff7373" />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Token Usage Trend Chart */}
          <Box mb="20px" p="20px" bg={boxBg} borderRadius="3xl" boxShadow="md">
            <Text fontSize="xl" fontWeight="bold" mb="10px" borderBottom="2px solid" borderColor={brandColor}>
              {t("Token Usage Trend")}
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={analyticsData.trends.token}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="user"
                  stackId="1"
                  name="User"
                  stroke="#8884d8"
                  fill="#8884d8"
                />
                <Area
                  type="monotone"
                  dataKey="assistant"
                  stackId="1"
                  name="Assistant"
                  stroke="#82ca9d"
                  fill="#82ca9d"
                />
                <Line
                  type="monotone"
                  dataKey="avg_per_message"
                  name="Avg Per Message"
                  stroke="#ff7300"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}

      {/* Chat Trends */}
      {chatTrendsData && (
        <Box mb="20px" p="20px" bg={boxBg} borderRadius="3xl" boxShadow="md">
          <Text fontSize="xl" fontWeight="bold" mb="10px" borderBottom="2px solid" borderColor={brandColor}>
            {t("Chat Trends")}
          </Text>

          {/* Daily or Monthly Graphs */}
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={
                timeFilter === "last_year" || timeFilter === "specific_year"
                  ? aggregateByMonth(chatTrendsData) // Aggregate data by month for yearly view
                  : chatTrendsData // Use daily data for monthly view
              }
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={timeFilter === "last_year" || timeFilter === "specific_year" ? "month" : "date"}
                tickFormatter={(value) =>
                  timeFilter === "last_year" || timeFilter === "specific_year"
                    ? t(`Month ${value}`) // Format month names
                    : value
                }
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="chat_count"
                name={t("Chat Count")}
                stroke="#8884d8"
                fill="#8884d8"
              />
              <Area
                type="monotone"
                dataKey="message_count"
                name={t("Message Count")}
                stroke="#82ca9d"
                fill="#82ca9d"
              />
              <Area
                type="monotone"
                dataKey="user_token_count"
                name={t("User Tokens")}
                stroke="#ffc658"
                fill="#ffc658"
              />
              <Area
                type="monotone"
                dataKey="assistant_token_count"
                name={t("Assistant Tokens")}
                stroke="#ff7300"
                fill="#ff7300"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}

      {messagesPerDay.length > 0 && (
        <Box mb="20px" p="20px" bg={boxBg} borderRadius="3xl" boxShadow="md">
          <Text fontSize="xl" fontWeight="bold" mb="10px" borderBottom="2px solid" borderColor={brandColor}>
            {t("Messages Per Day of the Week")}
          </Text>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={messagesPerDay}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Messages" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}
